/**
 * Emoji no desenho da Apple (ordem da dona, 2026-09-28) — `emoji-mart` com
 * `set="apple"`, o mesmo do PharuCut. Dados e seletor carregam na primeira
 * abertura; até lá o caractere nativo segura a tela.
 */
import React, { Suspense, lazy, useEffect, useRef, useState } from "react";

const Picker = lazy(() => import("@emoji-mart/react"));

let prontoPromessa = null;
let pronto = false;
const ouvintes = new Set();
// native (com tom de pele) → id e tom: o <em-emoji native> perde o tom escolhido.
const indice = new Map();
// id → o emoji em cada tom (1 = amarelo … 6); só os que têm tom trazem 6.
const tons = new Map();
const CHAVE_DO_TOM = "emoji-mart.skin";

/** Folha de 32 px (1,6 MB) no lugar da de 64 px (4,4 MB). */
export function folhaDeEmojis(set = "apple") {
  return `https://cdn.jsdelivr.net/npm/emoji-datasource-${set}@15.0.1/img/${set}/sheets-256/32.png`;
}

/** O tom de pele é de cada pessoa: vem do ERP e vai para a chave que o seletor lê. */
export function aplicarTomDePele(tom) {
  try {
    localStorage.setItem(CHAVE_DO_TOM, JSON.stringify(Math.min(6, Math.max(1, Number(tom) || 1))));
  } catch {
    /* sem storage */
  }
}

/** O tom guardado agora — o mesmo com que o seletor abre (1 = amarelo). */
export function tomDePele() {
  try {
    const tom = Number(JSON.parse(localStorage.getItem(CHAVE_DO_TOM)));
    return tom >= 1 && tom <= 6 ? tom : 1;
  } catch {
    return 1;
  }
}

/** O emoji no tom da pessoa quando ele tem tom (👍, 🙏); os outros voltam iguais. */
export function comTomDePele(native, tom = tomDePele()) {
  const r = resolverEmoji(native);
  const variantes = r && tons.get(r.id);
  return variantes && variantes.length === 6 ? variantes[tom - 1] : native;
}

export function resolverEmoji(native) {
  return indice.get(native) || indice.get(native.replace(/\uFE0F/g, "")) || null;
}

export function prepararEmojis() {
  if (!prontoPromessa) {
    prontoPromessa = Promise.all([import("emoji-mart"), import("@emoji-mart/data/sets/15/apple.json")]).then(([mart, dados]) => {
      // O conjunto APPLE traz as coordenadas na folha; o `native` (padrão do pacote) não — e todo emoji virava o primeiro sprite.
      mart.init({ data: dados.default, set: "apple", getSpritesheetURL: folhaDeEmojis });
      Object.values(dados.default.emojis).forEach((e) => {
        tons.set(e.id, (e.skins || []).map((s) => s.native));
        (e.skins || []).forEach((s, i) => {
          indice.set(s.native, { id: e.id, skin: i + 1 });
          indice.set(s.native.replace(/\uFE0F/g, ""), { id: e.id, skin: i + 1 });
        });
      });
      pronto = true;
      ouvintes.forEach((f) => f());
    });
  }
  return prontoPromessa;
}

export function useEmojisProntos() {
  const [estado, setEstado] = useState(pronto);
  useEffect(() => {
    if (pronto) return undefined;
    const avisar = () => setEstado(true);
    ouvintes.add(avisar);
    prepararEmojis();
    return () => ouvintes.delete(avisar);
  }, []);
  return estado;
}

export function EmojiApple({ native, size = "1.15em" }) {
  const ok = useEmojisProntos();
  if (!ok) return <span>{native}</span>;
  const r = resolverEmoji(native);
  if (r) return <em-emoji id={r.id} skin={r.skin} size={size} set="apple" />;
  return <em-emoji native={native} size={size} set="apple" />;
}

export function SeletorDeEmoji({ aoEscolher, aoFechar, alinhar = "esquerda", aoEscolherTom }) {
  const caixaRef = useRef(null);
  const [dados, setDados] = useState(null);
  // Trocar o tom no seletor e escolher um emoji sem tom (😂) não avisava o
  // ERP: o tom ficava só neste aparelho e voltava ao amarelo no próximo login.
  const tomAoAbrir = useRef(tomDePele());
  const aoEscolherTomRef = useRef(aoEscolherTom);
  aoEscolherTomRef.current = aoEscolherTom;
  useEffect(() => () => {
    const tom = tomDePele();
    if (tom !== tomAoAbrir.current) aoEscolherTomRef.current?.(tom);
  }, []);
  useEffect(() => {
    prepararEmojis().then(() => import("@emoji-mart/data/sets/15/apple.json")).then((d) => setDados(d.default));
    const aoClicar = (e) => {
      if (caixaRef.current && !caixaRef.current.contains(e.target)) aoFechar();
    };
    const aoTeclar = (e) => {
      if (e.key === "Escape") { e.preventDefault(); aoFechar(); }
    };
    const id = window.setTimeout(() => document.addEventListener("mousedown", aoClicar), 0);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aoFechar]);
  return (
    <div ref={caixaRef} className={`pchat-seletor-emoji ${alinhar}`} role="dialog" aria-label="Escolher emoji">
      {dados ? (
        <Suspense fallback={<div className="pchat-vazio">Carregando emojis…</div>}>
          <Picker data={dados} set="apple" getSpritesheetURL={folhaDeEmojis} locale="pt" theme="light" previewPosition="none" skinTonePosition="search" maxFrequentRows={2} onEmojiSelect={(e) => { if (e.skin && e.skin !== tomAoAbrir.current) { tomAoAbrir.current = e.skin; aoEscolherTom?.(e.skin); } aoEscolher(e.native); }} />
        </Suspense>
      ) : (
        <div className="pchat-vazio">Carregando emojis…</div>
      )}
    </div>
  );
}
